/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Settings Service - Super Admin Platform Settings API
 * ============================================================================
 *
 * Service layer for platform settings management API calls.
 *
 * ============================================================================
 */

import { get, put } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import { PlatformSetting, BatchUpdateSettingsRequest, SettingCategory } from '@/types/superadmin.types';

const BASE_URL = '/superadmin/settings';

/**
 * Settings service for Super Admin
 */
export const settingsService = {
  /**
   * Get all platform settings
   */
  async getSettings(): Promise<PlatformSetting[]> {
    const response = await get<ApiResponse<PlatformSetting[]>>(BASE_URL);
    return response.data;
  },

  /**
   * Get settings by category
   */
  async getSettingsByCategory(category: SettingCategory): Promise<PlatformSetting[]> {
    const response = await get<ApiResponse<PlatformSetting[]>>(BASE_URL, { category });
    return response.data;
  },

  /**
   * Get a single setting by key
   */
  async getSetting(key: string): Promise<PlatformSetting> {
    const response = await get<ApiResponse<PlatformSetting>>(`${BASE_URL}/${key}`);
    return response.data;
  },

  /**
   * Update a single setting
   */
  async updateSetting(id: string, value: string): Promise<PlatformSetting> {
    const response = await put<ApiResponse<PlatformSetting>>(`${BASE_URL}/${id}`, { settingValue: value });
    return response.data;
  },

  /**
   * Batch update multiple settings
   * Backend expects: Map<String, String> as { "SETTING_KEY": "value", ... }
   */
  async batchUpdateSettings(data: Record<string, string> | BatchUpdateSettingsRequest): Promise<void> {
    // Convert the array format to the map format expected by backend if needed
    let settingsMap: Record<string, string>;

    if ('settings' in data && Array.isArray((data as BatchUpdateSettingsRequest).settings)) {
      // Handle array format: { settings: [{ id, settingValue }] }
      settingsMap = {};
      (data as BatchUpdateSettingsRequest).settings.forEach(s => {
        settingsMap[s.id] = s.settingValue;
      });
    } else {
      // Handle map format directly: { "KEY": "value" }
      settingsMap = data as Record<string, string>;
    }
    await put<ApiResponse<void>>(`${BASE_URL}/batch`, settingsMap);
  },

  /**
   * Get all setting categories
   */
  async getCategories(): Promise<SettingCategory[]> {
    const response = await get<ApiResponse<SettingCategory[]>>(`${BASE_URL}/categories`);
    return response.data;
  },
};

export default settingsService;
